import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Label } from '@/shared/components/ui/label'
import { Character } from '@storyverse/shared'
import { useEffect, useState } from 'react'
import { useSaveCharacterMutation, useDeleteCharacterMutation } from '../queries/character.queries'
import { Trash2, Save, User, UserCircle } from 'lucide-react'

interface CharacterFormProps {
    character?: Character | null
    projectId: string
    onClose?: () => void
}

export function CharacterForm({ character, projectId, onClose }: CharacterFormProps) {
    const saveMutation = useSaveCharacterMutation()
    const deleteMutation = useDeleteCharacterMutation()

    const [formData, setFormData] = useState<Partial<Character>>({
        name: '',
        role: 'Protagonist',
        description: '',
        physicalAppearance: '',
        personality: '',
    })

    useEffect(() => {
        if (character) {
            setFormData(character)
        } else {
            setFormData({
                name: '',
                role: 'Protagonist',
                description: '',
                physicalAppearance: '',
                personality: '',
            })
        }
    }, [character])

    const handleSave = () => {
        if (!formData.name) return

        saveMutation.mutate({
            ...formData,
            projectId,
        } as Character, {
            onSuccess: () => {
                onClose?.()
            }
        })
    }

    const handleDelete = () => {
        if (character?.id && window.confirm('Are you sure you want to delete this character?')) {
            deleteMutation.mutate(character.id)
            onClose?.()
        }
    }

    return (
        <Card className="max-w-2xl mx-auto shadow-xl border-primary/20 bg-background/50 backdrop-blur-sm">
            <CardHeader className="flex flex-row items-center justify-between border-b pb-4">
                <CardTitle className="text-2xl font-bold flex items-center gap-2 text-primary">
                    <UserCircle className="w-6 h-6" />
                    {character ? `Edit Character: ${character.name}` : 'Create New Character'}
                </CardTitle>
                {character && (
                    <Button variant="ghost" size="icon" onClick={handleDelete} className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="w-5 h-5" />
                    </Button>
                )}
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="name">Character Name</Label>
                        <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g., John Doe"
                            className="bg-muted/30"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="role">Role</Label>
                        <Input
                            id="role"
                            value={formData.role}
                            onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                            placeholder="e.g., Protagonist, Antagonist, Supporting"
                            className="bg-muted/30"
                        />
                    </div>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="description">Short Bio / Description</Label>
                    <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="A brief summary of who they are..."
                        className="min-h-[80px] bg-muted/30"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <Label htmlFor="appearance">Physical Appearance</Label>
                        <Textarea
                            id="appearance"
                            value={formData.physicalAppearance}
                            onChange={(e) => setFormData(prev => ({ ...prev, physicalAppearance: e.target.value }))}
                            placeholder="Height, eye color, clothing style..."
                            className="min-h-[100px] bg-muted/30"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="personality">Personality Traits</Label>
                        <Textarea
                            id="personality"
                            value={formData.personality}
                            onChange={(e) => setFormData(prev => ({ ...prev, personality: e.target.value }))}
                            placeholder="Brave, cynical, optimistic, etc..."
                            className="min-h-[100px] bg-muted/30"
                        />
                    </div>
                </div>

                <div className="flex justify-end pt-4 gap-2">
                    {onClose && <Button variant="ghost" onClick={onClose}>Cancel</Button>}
                    <Button
                        onClick={handleSave}
                        disabled={saveMutation.isPending || !formData.name}
                        className="px-8"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        {saveMutation.isPending ? 'Saving...' : 'Save Character'}
                    </Button>
                </div>
            </CardContent>
        </Card>
    )
}
